import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import SearchableDropdown from '../common/SearchableDropdown';

const LearningOutcomeSelector = ({ learningAreaId, strandId, selectedOutcome, selectedOutcomes = [], multiple = false, onChange }) => {
    const { get } = useApi();
    const [strands, setStrands] = useState([]);
    const [selectedStrand, setSelectedStrand] = useState(null);
    const [subStrands, setSubStrands] = useState([]);
    const [selectedSubStrands, setSelectedSubStrands] = useState([]); // Array of substrand objects
    const [outcomes, setOutcomes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchStrands = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await get(`/api/cbc/learning-areas/${learningAreaId}/strands/`);
            setStrands(Array.isArray(data) ? data : []);
        } catch (err) {
            setError('Failed to load strands');
            console.error('Error fetching strands:', err);
        } finally {
            setLoading(false);
        }
    }, [get, learningAreaId]);

    const fetchOutcomesBatch = useCallback(async (subStrandIds) => {
        if (!subStrandIds || subStrandIds.length === 0) {
            setOutcomes([]);
            return;
        }

        setLoading(true);
        try {
            // Fetch outcomes for all selected substrands
            const promises = subStrandIds.map(id => get(`/api/cbc/sub-strands/${id}/learning-outcomes/`));
            const results = await Promise.all(promises);
            const allOutcomes = results.flat();
            
            // Deduplicate by ID
            const uniqueOutcomes = Array.from(new Map(allOutcomes.map(o => [o.id, o])).values());
            setOutcomes(uniqueOutcomes);
        } catch (err) {
            setError('Failed to load learning outcomes');
            console.error('Error fetching outcomes batch:', err);
        } finally {
            setLoading(false);
        }
    }, [get]);

    const handleSubStrandChange = useCallback(async (subStrandId, clearSelection = true, subStrandsList = null) => {
        const listToUse = subStrandsList || subStrands;
        const subStrand = listToUse.find(ss => ss.id === subStrandId || ss.id.toString() === subStrandId.toString());
        
        if (!subStrand) return;

        // If multiple mode, toggle/add. If single mode, replace.
        if (multiple) {
            setSelectedSubStrands(prev => {
                const exists = prev.find(s => s.id === subStrand.id);
                const next = exists ? prev.filter(s => s.id !== subStrand.id) : [...prev, subStrand];
                fetchOutcomesBatch(next.map(s => s.id));
                return next;
            });
        } else {
            setSelectedSubStrands([subStrand]);
            if (clearSelection) {
                onChange(null);
            }
            fetchOutcomesBatch([subStrand.id]);
        }
    }, [subStrands, onChange, multiple, fetchOutcomesBatch]);

    const handleStrandChange = useCallback(async (strandId, clearSelection = true) => {
        const strand = strands.find(s => s.id === strandId || s.id.toString() === strandId.toString());
        setSelectedStrand(strand);
        setSelectedSubStrands([]);
        setOutcomes([]);

        if (clearSelection) {
            onChange(null); // Only clear if user manually changed it
        }

        if (strandId) {
            setLoading(true);
            try {
                const data = await get(`/api/cbc/sub-strands/?strand=${strandId}`);
                const validatedData = Array.isArray(data) ? data : [];
                setSubStrands(validatedData);

                // If we are in edit mode, derive all selected sub-strands from outcomes
                const allOutcomes = Array.isArray(selectedOutcomes) ? selectedOutcomes : [];
                if (selectedOutcome) allOutcomes.push(selectedOutcome);
                
                const uniqueSubStrandIds = [...new Set(allOutcomes.map(o => o.sub_strand_id || o.sub_strand).filter(Boolean))];
                
                if (uniqueSubStrandIds.length > 0) {
                    const matchedSubStrands = validatedData.filter(ss => 
                        uniqueSubStrandIds.some(id => id.toString() === ss.id.toString())
                    );
                    
                    if (matchedSubStrands.length > 0) {
                        setSelectedSubStrands(matchedSubStrands);
                        fetchOutcomesBatch(matchedSubStrands.map(s => s.id));
                    }
                }
            } catch (err) {
                setError('Failed to load sub-strands');
                console.error('Error fetching sub-strands:', err);
            } finally {
                setLoading(false);
            }
        } else {
            setSubStrands([]);
        }
    }, [strands, onChange, get, selectedOutcome, multiple, selectedOutcomes, fetchOutcomesBatch]);

    // Fetch strands when learning area changes
    useEffect(() => {
        if (learningAreaId) {
            fetchStrands();
        } else {
            setStrands([]);
            setSelectedStrand(null);
            setSubStrands([]);
            setSelectedSubStrands([]);
            setOutcomes([]);
        }
    }, [learningAreaId, fetchStrands]);

    // React to external strand selection
    useEffect(() => {
        if (strandId && strands.length > 0) {
            const strand = strands.find(s => s.id === strandId || s.id.toString() === strandId.toString());
            if (strand && selectedStrand?.id !== strand.id) {
                handleStrandChange(strandId, false);
            }
        }
    }, [strandId, strands, selectedStrand?.id, handleStrandChange]);

    const handleOutcomeChange = (outcomeId) => {
        const outcome = outcomes.find(o => o.id === outcomeId || o.id.toString() === outcomeId.toString());
        if (outcome) {
            // Normalize to ensure sub_strand_id is present for persistence logic
            onChange({
                ...outcome,
                sub_strand_id: outcome.sub_strand_id || outcome.sub_strand
            });
        } else if (!multiple) {
            onChange(null);
        }
    };

    const toggleOutcome = (outcomeId) => {
        const outcome = outcomes.find(o => o.id === outcomeId || o.id.toString() === outcomeId.toString());
        if (!outcome) return;

        const safeSelected = Array.isArray(selectedOutcomes) ? selectedOutcomes : [];
        const isSelected = safeSelected.some(o => o.id === outcome.id || o.id.toString() === outcome.id.toString());
        if (isSelected) {
            onChange(safeSelected.filter(o => o.id !== outcome.id));
        } else {
            onChange([...safeSelected, {
                ...outcome,
                sub_strand_id: outcome.sub_strand_id || outcome.sub_strand
            }]);
        }
    };

    const removeOutcome = (outcomeId) => {
        const safeSelected = Array.isArray(selectedOutcomes) ? selectedOutcomes : [];
        onChange(safeSelected.filter(o => o.id !== outcomeId));
    };

    if (!learningAreaId) {
        return (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                    Please select a Learning Area first to choose a learning outcome.
                </p>
            </div>
        );
    }

    const safeSelected = Array.isArray(selectedOutcomes) ? selectedOutcomes : [];
    const currentStrandOutcomes = multiple ? safeSelected.filter(o => 
        subStrands.some(ss => ss.id === o.sub_strand_id || ss.id === o.sub_strand)
    ) : [];

    return (
        <div className="space-y-4">
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800">{error}</p>
                </div>
            )}

            {/* Curriculum Mapping Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Strand Selector (only if not provided by parent) */}
                {!strandId && (
                    <div>
                        <label className="block text-[11px] font-black text-[#18216D] uppercase tracking-widest mb-2 ml-1">
                            Curriculum Strand <span className="text-red-500">*</span>
                        </label>
                        <SearchableDropdown
                            options={strands}
                            selectedValues={selectedStrand ? [selectedStrand.id] : []}
                            onChange={(id) => handleStrandChange(id)}
                            placeholder="Select a strand..."
                            isMultiple={false}
                            displayKey="name"
                            disabled={loading || !strands || strands.length === 0}
                        />
                    </div>
                )}

                {/* Sub-Strand Selector (appears after strand selected) */}
                {selectedStrand && (
                    <div className="animate-in fade-in duration-300">
                        <label className="block text-[11px] font-black text-[#18216D] uppercase tracking-widest mb-2 ml-1">
                            {multiple ? 'Sub-Strands / Lessons' : 'Sub-Strand / Lesson'} <span className="text-red-500">*</span>
                        </label>
                        <SearchableDropdown
                            options={subStrands}
                            selectedValues={multiple ? selectedSubStrands.map(s => s.id) : (selectedSubStrands[0] ? [selectedSubStrands[0].id] : [])}
                            onChange={(id) => handleSubStrandChange(id)}
                            placeholder={multiple ? 'Choose lessons...' : 'Select a sub-strand...'}
                            isMultiple={multiple}
                            displayKey="name"
                            disabled={loading || !subStrands || subStrands.length === 0}
                        />
                        
                        {/* Sub-Strand Tags (Multiple Mode Only) */}
                        {multiple && selectedSubStrands.length > 0 && (
                            <div className="flex flex-col gap-2 mt-4">
                                {selectedSubStrands.map(ss => (
                                    <div key={ss.id} className="flex items-center justify-between p-3 bg-indigo-50/50 border border-indigo-100 rounded-xl w-full">
                                        <div>
                                            <p className="font-black text-[#18216D] text-xs">{ss.name}</p>
                                        </div>
                                        <button 
                                            type="button" 
                                            onClick={() => handleSubStrandChange(ss.id)}
                                            className="w-6 h-6 rounded-full bg-white border border-indigo-100 flex items-center justify-center text-indigo-400 hover:text-rose-500 hover:border-rose-200 transition-colors"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Learning Outcome Selector (appears after sub-strand selected) */}
            {selectedSubStrands.length > 0 && (
                <div className="animate-in fade-in duration-300">
                    <label className="block text-[11px] font-black text-[#18216D] uppercase tracking-widest mb-2 ml-1">
                        Learning Outcome <span className="text-red-500">*</span>
                    </label>
                    <SearchableDropdown
                        options={outcomes}
                        selectedValues={multiple ? (currentStrandOutcomes.map(o => o.id) || []) : (selectedOutcome ? [selectedOutcome.id] : [])}
                        onChange={(id) => multiple ? toggleOutcome(id) : handleOutcomeChange(id)}
                        placeholder={multiple ? 'Choose competencies...' : 'Select learning outcome...'}
                        isMultiple={multiple}
                        displayKey="description"
                        subDisplayKey="code"
                        disabled={loading || !outcomes || outcomes.length === 0}
                    />
                </div>
            )}

            {/* Selected Outcomes Tags (Multiple Mode) */}
            {multiple && currentStrandOutcomes.length > 0 && (
                <div className="flex flex-col gap-2 mt-4 animate-in fade-in duration-300">
                    {currentStrandOutcomes.map((outcome) => (
                        <div key={outcome.id} className="flex items-center justify-between p-3 bg-indigo-50/50 border border-indigo-100 rounded-xl w-full">
                            <div>
                                <p className="font-black text-[#18216D] text-xs">{outcome.description}</p>
                                <p className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest mt-0.5">{outcome.code}</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => removeOutcome(outcome.id)}
                                className="w-6 h-6 rounded-full bg-white border border-indigo-100 flex items-center justify-center text-indigo-400 hover:text-rose-500 hover:border-rose-200 transition-colors shrink-0"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Selected Outcome Preview (Single Mode) */}
            {!multiple && selectedOutcome && (
                <div className="bg-[#18216D]/5 border border-[#18216D]/10 rounded-2xl p-6 animate-in slide-in-from-top-4 duration-300">
                    <h4 className="text-[10px] font-black text-[#18216D] uppercase tracking-widest mb-3">Selected Learning Outcome</h4>
                    <p className="text-sm font-bold text-gray-900 mb-4 italic leading-relaxed">"{selectedOutcome.description}"</p>
                    <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-widest">
                        <span className="bg-[#18216D] text-white px-2 py-1 rounded">
                            {selectedOutcome.code}
                        </span>
                        <span className="text-slate-400">
                            {selectedStrand?.name} <i className="fas fa-chevron-right text-[8px] mx-1" /> {selectedSubStrands[0]?.name}
                        </span>
                    </div>
                </div>
            )}

            {/* Loading Indicator */}
            {loading && (
                <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#18216D]/10 border-t-[#18216D] shadow-sm"></div>
                    <span className="ml-4 text-[10px] font-black text-[#18216D] uppercase tracking-widest">Synchronizing...</span>
                </div>
            )}
        </div>
    );
};

export default LearningOutcomeSelector;
